import React, { ReactElement, ReactNode, useCallback, useMemo } from 'react';
import { useWizardMultistepReducer } from './WizardMultistepReducer';
import { WizardStep } from '../../types/WizardSteps';
import { WizardNavigationProvider } from './WizardNavigationProvider';
import { Step, Phase } from './WizardMultistepTypes';
import { stepIsCompletedOrSkipped } from './wizardMultistepHelpers';

export type WizardMultistepValue = {
    currentStepIndex: number;
    currentProgress: number;
    currentStep: Step | null;
    stepsChronologically: Array<Step>;
    latestStep: Step | null;
    registerStep: (
        payload: Partial<Step> & {
            id: WizardStep;
        },
    ) => void;
    goToNextStep: () => void;
    goToPreviousStep: () => void;
    jumpToStep: (id: WizardStep) => void;
    setStepCompleted: (id: WizardStep, completed: boolean) => void;
    setStepSkipped: (id: WizardStep, skipped: boolean) => void;
    setNextStepId: (id: WizardStep, nextStepId: WizardStep) => void;
    phases: Array<Phase>;
    modifyPhase: (index: number, phase: Partial<Phase>) => void;
    modifyMultipleSteps: (steps: { [key in WizardStep]?: Partial<Step> }) => void;
    setStepsSkippable: (stepIds: Array<WizardStep>, value: boolean) => void;
};

export const WizardMultistepContext = React.createContext<WizardMultistepValue | null>(null);

type WizardMultistepProviderProps = {
    children: ReactNode;
    initialStepId?: WizardStep | null;
    phases?: Array<Phase>;
};

function WizardMultistepProvider({
    children,
    initialStepId,
    phases,
}: WizardMultistepProviderProps): ReactElement<'div'> | null {
    const [state, dispatch] = useWizardMultistepReducer({
        initialStepId: initialStepId || null,
        phases: phases ? phases : [],
    });

    const stepsChronologically = useMemo(() => {
        const { initialStepId, steps } = state;
        if (!initialStepId) {
            return [];
        }
        let currentStep = steps[initialStepId];
        const stepsArr: Array<Step> = [];
        while (currentStep) {
            stepsArr.push(currentStep);
            if (!currentStep.nextStepId) {
                break;
            }
            currentStep = currentStep = steps[currentStep.nextStepId];
        }
        return stepsArr;
    }, [state.steps, state.initialStepId]);

    const currentStepIndex = useMemo(() => {
        const { currentStepId } = state;
        const i = stepsChronologically.findIndex((step) => step.id === currentStepId);
        return Math.max(0, i);
    }, [stepsChronologically, state.currentStepId]);

    const currentStep = useMemo(() => {
        return stepsChronologically[currentStepIndex];
    }, [stepsChronologically, currentStepIndex]);

    const latestStep = useMemo(() => {
        const { steps } = state;
        let step: Step | null = currentStep;
        while (stepIsCompletedOrSkipped(step) && step.nextStepId && steps[step.nextStepId]) {
            step = steps[step.nextStepId] || null;
        }
        return step;
    }, [currentStep, state.steps]);

    const currentProgress = useMemo(() => {
        const { currentStepId } = state;
        const necessaryNumberOfSteps = stepsChronologically.length;
        const currentStepIndex = stepsChronologically.findIndex((step) => step.id === currentStepId);
        if (currentStepIndex < 0 || necessaryNumberOfSteps - 1 === 0) {
            return 0;
        }
        return Math.round((currentStepIndex / (necessaryNumberOfSteps - 1)) * 100);
    }, [stepsChronologically, currentStep]);

    const registerStep = useCallback(
        (
            payload: Partial<Step> & {
                id: WizardStep;
            },
        ) => {
            dispatch({
                type: 'REGISTER_STEP',
                payload,
            });
        },
        [],
    );

    const jumpToStep = useCallback((id: WizardStep) => {
        dispatch({
            type: 'JUMP_TO_STEP',
            payload: { id },
        });
    }, []);

    const goToNextStep = useCallback(() => {
        if (!currentStep || !currentStep.completed || !currentStep.nextStepId) {
            return;
        }
        jumpToStep(currentStep.nextStepId);
    }, [jumpToStep, currentStep]);

    const goToPreviousStep = useCallback(() => {
        const previousStepIndex = currentStepIndex - 1;
        if (previousStepIndex < 0) {
            return;
        }
        const previousStep = stepsChronologically[previousStepIndex];
        jumpToStep(previousStep.id);
    }, [jumpToStep, stepsChronologically, currentStepIndex]);

    const setStepCompleted = useCallback((id: WizardStep, completed: boolean) => {
        dispatch({
            type: 'MODIFY_STEP',
            payload: { id, completed },
        });
    }, []);

    const setStepSkipped = useCallback((id: WizardStep, skipped: boolean) => {
        dispatch({
            type: 'MODIFY_STEP',
            payload: { id, skipped },
        });
    }, []);

    const setNextStepId = useCallback((id: WizardStep, nextStepId: WizardStep) => {
        dispatch({
            type: 'MODIFY_STEP',
            payload: { id, nextStepId },
        });
    }, []);

    const modifyMultipleSteps = useCallback((steps: { [key in WizardStep]?: Partial<Step> }) => {
        dispatch({
            type: 'MODIFY_MULTIPLE_STEPS',
            payload: { steps },
        });
    }, []);

    const setStepsSkippable = useCallback((steps: Array<WizardStep>, skippable) => {
        const skippableSteps = steps.reduce<{ [key in WizardStep]?: { skippable: boolean } }>((acc, id) => {
            return {
                ...acc,
                [id]: {
                    skippable,
                },
            };
        }, {});
        dispatch({
            type: 'MODIFY_MULTIPLE_STEPS',
            payload: { steps: skippableSteps },
        });
    }, []);

    const modifyPhase = useCallback((index: number, data: Partial<Phase>) => {
        dispatch({
            type: 'MODIFY_PHASE',
            payload: { index, data },
        });
    }, []);

    return (
        <WizardMultistepContext.Provider
            value={{
                currentStepIndex,
                currentProgress,
                currentStep,
                registerStep,
                goToNextStep,
                goToPreviousStep,
                jumpToStep,
                setStepCompleted,
                setStepSkipped,
                setNextStepId,
                latestStep,
                stepsChronologically,
                phases: state.phases,
                modifyPhase,
                modifyMultipleSteps,
                setStepsSkippable,
            }}
        >
            <WizardNavigationProvider>{children}</WizardNavigationProvider>
        </WizardMultistepContext.Provider>
    );
}

export { WizardMultistepProvider };
